import {
    updateTabField,
    buildField,
    formatJson,
    getFieldValueIfDirty,
    isFieldDirty,
    updateTabFieldValidators,
    updateTabFieldArray
} from '../../common/utils';
import { SUBCATEGORIES } from '../../common/categories';
import {
    MAINTENANCE_FIELDS,
    ADVANCED_CONFIG_FIELDS,
    CONFIG_SPECIFIC_NODE_FIELDS,
    ELASTIC_CONFIG_FIELDS
} from '../../common/fields';
import * as actions from './advancedActions';
import { commonActionsReducer } from '../../common/actions';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const { MAINTENANCE, ADVANCED_CONFIG, ELASTIC_CONFIG } = SUBCATEGORIES;
const {
    NODE_LOG_LEVEL,
    LOG_LEVEL,
    PUSH_SERVICES_LOG_LEVEL,
    OFFLINE_MODE,
    SEND_DIAGNOSTICS,
    SEND_DIAGNOSTICS_ANON,
    AUTO_AD_ADAPTER_UPGRADE,
    ALLOW_AZURE_OPENAI_USAGE,
    ADD_PASSWORD_TO_LOGS,
    CLIENT_LOG_PASSWORD,
    ALLOW_SEND_LOGS_TO_SILVERHUB,
    EXPIRE_SEND_LOGS_TO_SILVERHUB
} = MAINTENANCE_FIELDS;

const { CONFIG_ADMIN_CONSOLE, CONFIG_ALL_NODES, CONFIG_NODE } = ADVANCED_CONFIG_FIELDS;
const { CONFIG } = CONFIG_SPECIFIC_NODE_FIELDS;
const { THRESHOLD_PERCENTAGE, EVENT_THRESHOLD_PERCENTAGE, DISCONNECTIONS_FOR_NOTIFY, NUM_OF_SHARDS } =
    ELASTIC_CONFIG_FIELDS;

export const fieldsReducer = (state, action) => {
    switch (action.type) {
        case actions.UPDATE_MAINTENANCE:
            if (action.payload.fieldId === ADD_PASSWORD_TO_LOGS.ID) {
                const newState = updateTabFieldValidators(
                    state,
                    MAINTENANCE.ID,
                    CLIENT_LOG_PASSWORD.ID,
                    action.payload.value ? { required: true, password: true } : {}
                );
                return updateTabField(newState, MAINTENANCE.ID, action.payload.fieldId, action.payload.value);
            }
            return updateTabField(state, MAINTENANCE.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_NODES_SPECIFIC_CONFIG:
            return updateTabFieldArray(
                state,
                ADVANCED_CONFIG.ID,
                CONFIG_NODE.ID,
                action.payload.fieldIndex,
                CONFIG.ID,
                action.payload.value
            );
        case actions.UPDATE_ADVANCED_CONFIG:
            return updateTabField(state, ADVANCED_CONFIG.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_ELASTIC_CONFIG:
            return updateTabField(state, ELASTIC_CONFIG.ID, action.payload.fieldId, action.payload.value);
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings, schemas, towerSchema, isCloudOnly }) => {
    const state = {
        [MAINTENANCE.ID]: {
            id: MAINTENANCE.ID,
            name: MAINTENANCE.NAME,
            fields: {
                [NODE_LOG_LEVEL.ID]: {
                    ...buildField(NODE_LOG_LEVEL, settings.NodeLogLevel),
                    options: settings.NodeLogLevelOptions
                },
                [LOG_LEVEL.ID]: { ...buildField(LOG_LEVEL, settings.LogLevel), options: settings.LogLevelOptions },
                [PUSH_SERVICES_LOG_LEVEL.ID]: {
                    ...buildField(PUSH_SERVICES_LOG_LEVEL, settings.PushServicesLogLevel),
                    options: settings.NodeLogLevelOptions
                },
                [OFFLINE_MODE.ID]: buildField(
                    OFFLINE_MODE,
                    settings.OfflineMode,
                    {},
                    { restartNeededIfDisabled: true }
                ),
                [SEND_DIAGNOSTICS.ID]: buildField(SEND_DIAGNOSTICS, settings.DumperSendDailyAutomatically),
                [SEND_DIAGNOSTICS_ANON.ID]: buildField(SEND_DIAGNOSTICS_ANON, settings.DumperSendAutoAnonymized),
                [AUTO_AD_ADAPTER_UPGRADE.ID]: buildField(AUTO_AD_ADAPTER_UPGRADE, settings.AutoADAdapterUpgrade),
                [ALLOW_AZURE_OPENAI_USAGE.ID]: buildField(ALLOW_AZURE_OPENAI_USAGE, settings.AllowAzureOpenAiUsage),
                [ALLOW_SEND_LOGS_TO_SILVERHUB.ID]: buildField(
                    ALLOW_SEND_LOGS_TO_SILVERHUB,
                    settings.AllowSendLogsToSilverhub
                ),
                [EXPIRE_SEND_LOGS_TO_SILVERHUB.ID]: buildField(
                    EXPIRE_SEND_LOGS_TO_SILVERHUB,
                    settings.ExpireSendLogsToSilverhubOn
                ),
                [ADD_PASSWORD_TO_LOGS.ID]: buildField(ADD_PASSWORD_TO_LOGS, settings.addPasswordToLogs),
                [CLIENT_LOG_PASSWORD.ID]: buildField(
                    CLIENT_LOG_PASSWORD,
                    settings.ClientLogPassword,
                    settings.addPasswordToLogs ? { required: true, password: true } : {}
                )
            },
            validators: {}
        },
        [ADVANCED_CONFIG.ID]: {
            id: ADVANCED_CONFIG.ID,
            name:
                settings.systemMode === SYSTEM_MODES.SAAS_SUPPORT || isCloudOnly
                    ? ADVANCED_CONFIG.SAAS_SUPPORT_NAME
                    : ADVANCED_CONFIG.NAME,
            fields: {
                [CONFIG_ADMIN_CONSOLE.ID]: buildField(
                    CONFIG_ADMIN_CONSOLE,
                    formatJson(settings.TowerCustomConfig ? JSON.stringify(settings.TowerCustomConfig) : ''),
                    {
                        isJson: true,
                        jsonSchema: towerSchema,
                        phoenixUrl: true
                    },
                    { restartNeeded: true }
                ),
                [CONFIG_ALL_NODES.ID]: buildField(
                    CONFIG_ALL_NODES,
                    formatJson(
                        settings.GlobalNodesCustomConfig ? JSON.stringify(settings.GlobalNodesCustomConfig) : ''
                    ),
                    {
                        isJson: true,
                        jsonSchema: schemas.global
                    },
                    { restartAllNodes: true }
                ),
                [CONFIG_NODE.ID]: buildField(CONFIG_NODE, mapNodesConfig(settings, schemas))
            },
            validators: {}
        },
        [ELASTIC_CONFIG.ID]: {
            id: ELASTIC_CONFIG.ID,
            name: ELASTIC_CONFIG.NAME,
            fields: {
                [THRESHOLD_PERCENTAGE.ID]: buildField(
                    THRESHOLD_PERCENTAGE,
                    settings.ElasticStorageThresholdPercentage,
                    { required: true, isNumeric: true, minValue: 1, maxValue: 100 }
                ),
                [EVENT_THRESHOLD_PERCENTAGE.ID]: buildField(
                    EVENT_THRESHOLD_PERCENTAGE,
                    settings.ElasticStorageEventThresholdPercentage,
                    { required: true, isNumeric: true, minValue: 1, maxValue: 100 }
                ),
                [DISCONNECTIONS_FOR_NOTIFY.ID]: buildField(
                    DISCONNECTIONS_FOR_NOTIFY,
                    settings.maxElasticsearchDisconnectionsForNotify,
                    { required: true, isNumeric: true, minValue: 1, maxValue: 100 }
                ),
                [NUM_OF_SHARDS.ID]: buildField(NUM_OF_SHARDS, settings.NumberOfShardsOfAuthLogs, {
                    required: true,
                    isNumeric: true,
                    minValue: 1,
                    maxValue: 100
                })
            },
            validators: {}
        }
    };

    return state;
};

const mapNodesConfig = (settings, schemas) => {
    const nodesConfigArr = [];
    const nodesConfig = settings.NodesConfigurations;
    const sortedNodes = Object.keys(nodesConfig).sort();

    for (const nodeId of sortedNodes) {
        const nodeConfig = nodesConfig[nodeId];
        nodesConfigArr.push({
            [CONFIG.ID]: buildField(
                { ID: nodeConfig.id, NAME: nodeConfig.Name },
                formatJson(nodeConfig.CustomConfigurations ? JSON.stringify(nodeConfig.CustomConfigurations) : ''),
                {
                    isJson: true,
                    jsonSchema: schemas[nodeId]
                },
                { restartNode: true }
            )
        });
    }

    return nodesConfigArr;
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {};
    if (saveAll || selectedTab === MAINTENANCE.ID) {
        const maintenanceFields = fields[MAINTENANCE.ID].fields;
        newSettings.NodeLogLevel = getFieldValueIfDirty(maintenanceFields[NODE_LOG_LEVEL.ID]);
        newSettings.LogLevel = getFieldValueIfDirty(maintenanceFields[LOG_LEVEL.ID]);
        newSettings.PushServicesLogLevel = getFieldValueIfDirty(maintenanceFields[PUSH_SERVICES_LOG_LEVEL.ID]);
        newSettings.OfflineMode = getFieldValueIfDirty(maintenanceFields[OFFLINE_MODE.ID]);
        newSettings.DumperSendDailyAutomatically = getFieldValueIfDirty(maintenanceFields[SEND_DIAGNOSTICS.ID]);
        newSettings.DumperSendAutoAnonymized = getFieldValueIfDirty(maintenanceFields[SEND_DIAGNOSTICS_ANON.ID]);
        newSettings.AutoADAdapterUpgrade = getFieldValueIfDirty(maintenanceFields[AUTO_AD_ADAPTER_UPGRADE.ID]);
        newSettings.AllowAzureOpenAiUsage = getFieldValueIfDirty(maintenanceFields[ALLOW_AZURE_OPENAI_USAGE.ID]);
        newSettings.AllowSendLogsToSilverhub = getFieldValueIfDirty(maintenanceFields[ALLOW_SEND_LOGS_TO_SILVERHUB.ID]);
        newSettings.ExpireSendLogsToSilverhubOn = getFieldValueIfDirty(
            maintenanceFields[EXPIRE_SEND_LOGS_TO_SILVERHUB.ID]
        );
        newSettings.addPasswordToLogs = getFieldValueIfDirty(maintenanceFields[ADD_PASSWORD_TO_LOGS.ID]);
        newSettings.ClientLogPassword = getFieldValueIfDirty(maintenanceFields[CLIENT_LOG_PASSWORD.ID]);
    }
    if (saveAll || selectedTab === ADVANCED_CONFIG.ID) {
        const advancedFields = fields[ADVANCED_CONFIG.ID].fields;
        newSettings.TowerCustomConfig = getFieldValueIfDirty(
            advancedFields[CONFIG_ADMIN_CONSOLE.ID],
            parseToJson(advancedFields[CONFIG_ADMIN_CONSOLE.ID].value)
        );
        newSettings.GlobalNodesCustomConfig = getFieldValueIfDirty(
            advancedFields[CONFIG_ALL_NODES.ID],
            parseToJson(advancedFields[CONFIG_ALL_NODES.ID].value)
        );

        const isConfigSpecificNodesDirty = isFieldDirty(advancedFields[CONFIG_NODE.ID]);

        if (isConfigSpecificNodesDirty) {
            const nodesConfigFields = advancedFields[CONFIG_NODE.ID].value;
            const nodeConfigDirtyFieldsIds = nodesConfigFields.filter(
                (nodeConfig) =>
                    nodeConfig[CONFIG.ID].id !== CONFIG_ADMIN_CONSOLE.ID &&
                    nodeConfig[CONFIG.ID].id !== CONFIG_ALL_NODES.ID &&
                    isFieldDirty(nodeConfig[CONFIG.ID])
            );

            newSettings.NodesConfigurations = { ...currentSettings.NodesConfigurations };
            for (const node of nodeConfigDirtyFieldsIds) {
                const nodeId = node[CONFIG.ID].id;
                const jsonConfigField = node[CONFIG.ID].value;
                newSettings.NodesConfigurations[nodeId] = { ...currentSettings.NodesConfigurations[nodeId] };
                newSettings.NodesConfigurations[nodeId].CustomConfigurations = parseToJson(jsonConfigField);
            }
        }
    }
    if (saveAll || selectedTab === ELASTIC_CONFIG.ID) {
        const elasticFields = fields[ELASTIC_CONFIG.ID].fields;
        newSettings.ElasticStorageThresholdPercentage = getFieldValueIfDirty(elasticFields[THRESHOLD_PERCENTAGE.ID]);
        newSettings.ElasticStorageEventThresholdPercentage = getFieldValueIfDirty(
            elasticFields[EVENT_THRESHOLD_PERCENTAGE.ID]
        );
        newSettings.maxElasticsearchDisconnectionsForNotify = getFieldValueIfDirty(
            elasticFields[DISCONNECTIONS_FOR_NOTIFY.ID]
        );
        newSettings.NumberOfShardsOfAuthLogs = getFieldValueIfDirty(elasticFields[NUM_OF_SHARDS.ID]);
    }
    return newSettings;
};

function parseToJson(value) {
    if (!value) {
        return '';
    }

    try {
        return JSON.parse(value);
    } catch (err) {
        console.error(err);
        return '';
    }
}
